import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import adminSchemaApi from '../api/adminSchemaApi';
import {
  clampEntitlementsToPlan,
  DEFAULT_ENTITLEMENTS_CONFIG,
  entitlementsFromPlanConfig,
  normalizeEntitlements,
  normalizePlanConfig,
} from './AdminPlanConfigConstants';
import EntitlementsForm from './EntitlementsForm';
import '../styles/admin-theme.css';

const emptyLicenseForm = () => ({
  license_type_id: '',
  category: '',
  is_active: true,
});

const AdminPlanLicenses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [planLicenses, setPlanLicenses] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [editingPlanLicense, setEditingPlanLicense] = useState(null);
  const [licenseForm, setLicenseForm] = useState(emptyLicenseForm());
  const [entitlementsConfig, setEntitlementsConfig] = useState(() => JSON.parse(JSON.stringify(DEFAULT_ENTITLEMENTS_CONFIG)));
  const [savingLicense, setSavingLicense] = useState(false);
  const [errorLicense, setErrorLicense] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [standardObjects, setStandardObjects] = useState([]);
  const [standardTabs, setStandardTabs] = useState([]);

  const planConfig = useMemo(
    () => (plan ? normalizePlanConfig(plan.config || {}) : null),
    [plan]
  );

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const planData = await adminSchemaApi.fetchPlanById(id);
      setPlan(planData);
      const packages = planData?.config?.enabledPackages ?? [];
      const [licenses, types, objects, tabs] = await Promise.all([
        adminSchemaApi.fetchPlanLicenses(id),
        adminSchemaApi.fetchLicenseTypes(),
        adminSchemaApi.fetchStandardObjects(packages).catch(() => []),
        adminSchemaApi.fetchStandardTabs(packages).catch(() => []),
      ]);
      setPlanLicenses(Array.isArray(licenses) ? licenses : []);
      setLicenseTypes(Array.isArray(types) ? types : []);
      setStandardObjects(Array.isArray(objects) ? objects : []);
      setStandardTabs(Array.isArray(tabs) ? tabs : []);
    } catch (err) {
      setError('Failed to load plan licenses.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  const loadPlanLicenses = useCallback(async () => {
    if (!id) return;
    try {
      const licenses = await adminSchemaApi.fetchPlanLicenses(id);
      setPlanLicenses(Array.isArray(licenses) ? licenses : []);
    } catch {
      // keep existing list
    }
  }, [id]);

  const openAddLicense = () => {
    setEditingPlanLicense(null);
    setLicenseForm(emptyLicenseForm());
    const base = entitlementsFromPlanConfig(planConfig);
    setEntitlementsConfig(clampEntitlementsToPlan(base, planConfig));
    setErrorLicense('');
    setShowLicenseModal(true);
  };

  const openEditLicense = (row) => {
    setEditingPlanLicense(row);
    setLicenseForm({
      license_type_id: row.license_type_id || '',
      is_active: row.is_active !== false,
    });
    setEntitlementsConfig(clampEntitlementsToPlan(normalizeEntitlements(row.entitlements), planConfig));
    setErrorLicense('');
    setShowLicenseModal(true);
  };

  const closeLicenseModal = () => {
    setShowLicenseModal(false);
    setEditingPlanLicense(null);
    setLicenseForm(emptyLicenseForm());
    setErrorLicense('');
  };

  const handleSaveLicense = async (e) => {
    e.preventDefault();
    setErrorLicense('');
    if (!editingPlanLicense && !licenseForm.license_type_id) {
      setErrorLicense('Select a license type.');
      return;
    }
    setSavingLicense(true);
    const payloadEntitlements = clampEntitlementsToPlan(entitlementsConfig, planConfig);
    try {
      if (editingPlanLicense) {
        await adminSchemaApi.updatePlanLicense(id, editingPlanLicense.id, {
          entitlements: payloadEntitlements,
          is_active: licenseForm.is_active,
        });
        setMessage('Plan license updated.');
      } else {
        await adminSchemaApi.createPlanLicense(id, {
          license_type_id: licenseForm.license_type_id,
          entitlements: payloadEntitlements,
          is_active: licenseForm.is_active,
        });
        setMessage('Plan license added.');
      }
      closeLicenseModal();
      await loadPlanLicenses();
    } catch (err) {
      setErrorLicense(err?.message || 'Failed to save plan license');
    } finally {
      setSavingLicense(false);
    }
  };

  const handleDeleteLicense = async (row) => {
    if (!window.confirm(`Remove "${row.license_type_name || row.license_type_code}" from this plan?`)) return;
    setDeletingId(row.id);
    setError('');
    try {
      await adminSchemaApi.deletePlanLicense(id, row.id);
      setMessage('Plan license removed.');
      await loadPlanLicenses();
    } catch (err) {
      setError(err?.message || 'Failed to delete plan license');
    } finally {
      setDeletingId(null);
    }
  };

  const licenseTypesNotInPlan = useMemo(() => {
    const used = new Set(planLicenses.map((pl) => pl.license_type_id));
    return licenseTypes.filter((lt) => !used.has(lt.id));
  }, [licenseTypes, planLicenses]);

  const licenseTypesFilteredByCategory = useMemo(() => {
    const list = licenseTypesNotInPlan;
    const cat = licenseForm.category?.trim().toUpperCase();
    if (!cat || cat === 'ALL') return list;
    return list.filter((lt) => (lt.category || '').toUpperCase() === cat);
  }, [licenseTypesNotInPlan, licenseForm.category]);

  if (!id) {
    navigate('/admin/plans', { replace: true });
    return null;
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <Link to="/admin/plans" className="btn btn-outline-secondary btn-sm me-2">
            ← Plans
          </Link>
          <Link to={`/admin/plans/${id}`} className="btn btn-outline-secondary btn-sm me-2">
            Config
          </Link>
          <h2 className="admin-title mb-1 d-inline">Plan licenses</h2>
          {plan && (
            <p className="admin-subtitle mb-0">
              {plan.name || plan.code} ({plan.code})
            </p>
          )}
        </div>
        <Button size="sm" className="admin-primary-btn" onClick={openAddLicense} disabled={loading || licenseTypesNotInPlan.length === 0}>
          Add license
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {loading ? (
        <div className="admin-subtitle">Loading...</div>
      ) : !plan ? (
        <div className="admin-subtitle">Plan not found.</div>
      ) : (
        <>
          <p className="admin-subtitle mb-3">
            Attach license types to this plan and configure restrictions per license. Each license type can be added once.
          </p>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>License type</th>
                <th>Category</th>
                <th>Entitlements</th>
                <th>Active</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {planLicenses.map((row) => (
                <tr key={row.id}>
                  <td>{row.license_type_name || row.license_type_code || '—'}</td>
                  <td>{row.license_type_category || '—'}</td>
                  <td>{row.entitlements != null && typeof row.entitlements === 'object' && Object.keys(row.entitlements || {}).length > 0 ? 'Configured' : '—'}</td>
                  <td>{row.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEditLicense(row)} title="Edit">
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteLicense(row)}
                      disabled={deletingId === row.id}
                      title="Remove"
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
              {planLicenses.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No licenses. Click Add license to attach a license type.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {licenseTypesNotInPlan.length === 0 && planLicenses.length > 0 && (
            <p className="text-muted small">All license types are already added to this plan.</p>
          )}
        </>
      )}

      <Modal show={showLicenseModal} onHide={closeLicenseModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{editingPlanLicense ? 'Edit plan license' : 'Add plan license'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveLicense}>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {errorLicense && <Alert variant="danger">{errorLicense}</Alert>}
            {!editingPlanLicense && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Select
                    value={licenseForm.category || 'ALL'}
                    onChange={(e) => setLicenseForm((p) => ({ ...p, category: e.target.value, license_type_id: '' }))}
                  >
                    <option value="ALL">All</option>
                    <option value="USER">USER</option>
                    <option value="FEATURE">FEATURE</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>License type</Form.Label>
                  <Form.Select
                    value={licenseForm.license_type_id}
                    onChange={(e) => setLicenseForm((p) => ({ ...p, license_type_id: e.target.value }))}
                    required
                  >
                    <option value="">Select...</option>
                    {licenseTypesFilteredByCategory.map((lt) => (
                      <option key={lt.id} value={lt.id}>
                        {lt.name || lt.code} ({lt.code}){lt.category ? ` — ${lt.category}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            )}
            {editingPlanLicense && (
              <Form.Group className="mb-3">
                <Form.Label>License type</Form.Label>
                <Form.Control
                  plaintext
                  readOnly
                  value={`${editingPlanLicense.license_type_name || editingPlanLicense.license_type_code || ''}${editingPlanLicense.license_type_category ? ` (${editingPlanLicense.license_type_category})` : ''}`}
                />
              </Form.Group>
            )}
            <Form.Check
              type="checkbox"
              id="plan-license-active"
              label="Active"
              checked={licenseForm.is_active}
              onChange={(e) => setLicenseForm((p) => ({ ...p, is_active: e.target.checked }))}
              className="mb-3"
            />
            <h6 className="mt-3 mb-2">Entitlements (within plan limits — same structure as company license)</h6>
            {planConfig && (
              <EntitlementsForm
                entitlementsConfig={entitlementsConfig}
                onChange={setEntitlementsConfig}
                planConfig={planConfig}
                disabled={savingLicense}
                standardObjects={standardObjects}
                standardTabs={standardTabs}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeLicenseModal}>
              Cancel
            </Button>
            <Button type="submit" className="admin-primary-btn" disabled={savingLicense}>
              {savingLicense ? 'Saving…' : editingPlanLicense ? 'Save' : 'Add'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AdminPlanLicenses;
